import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const FrankChenConcessionsSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full max-w-[1100px] z-10 relative flex flex-col items-center text-center">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-4"
        >
          Special concession for Dr. Frank Chen
        </motion.p>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="text-4xl lg:text-[56px] font-bold text-slate-900 tracking-tight leading-[1.08] max-w-[920px]"
        >
          We'll set you up as{" "}
          <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            5 locations.
          </span>
          <br />
          We'll only{" "}
          <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            charge you for 3.
          </span>
        </motion.h2>

        {/* Hero visual — 5 → 3 */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.18 }}
          className="mt-10 flex items-center gap-8"
        >
          {/* 5 location dots */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0.6, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 + i * 0.05, type: "spring", stiffness: 220, damping: 18 }}
                  className="h-12 w-12 rounded-xl bg-white border border-slate-200 shadow-[0_6px_18px_-10px_rgba(15,23,42,0.25)] flex items-center justify-center"
                >
                  <div className="h-5 w-5 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600" />
                </motion.div>
              ))}
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Provisioned · 5 locations
            </p>
          </div>

          {/* Arrow */}
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
          >
            <ArrowRight className="h-6 w-6" />
          </motion.div>

          {/* 3 billed dots */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2.5">
              {Array.from({ length: 3 }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0.6, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.85 + i * 0.08, type: "spring", stiffness: 220, damping: 18 }}
                  className="h-12 w-12 rounded-xl bg-white border-2 border-blue-400 ring-2 ring-blue-100 shadow-[0_8px_22px_-8px_rgba(59,130,246,0.45)] flex items-center justify-center"
                >
                  <div className="h-5 w-5 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600" />
                </motion.div>
              ))}
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700">
              Billed · 3 locations
            </p>
          </div>
        </motion.div>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="mt-8 text-[15px] text-slate-600 leading-relaxed max-w-[680px]"
        >
          Your operating model is different from a typical multi-location DSO.
          You'll get the full 5-location platform footprint — billed as 3.
        </motion.p>
      </div>
    </div>
  );
};

export default FrankChenConcessionsSlide;
